import React, { useEffect, useRef, useState, useCallback } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import QuillCursors from 'quill-cursors';
import { useParams, useLocation } from "react-router-dom";
import { fetchDocument, generateMedADocsPath } from "./documentUtilities";
import { bucketName_files, getWebSocketURL, getBackendURL } from "../components/env";
import { getSupabase } from "../components/supabaseClient";
import { generateNonBlueColor } from "../hooks/useCollaboration";
import {
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Select,
} from "@chakra-ui/react";
import { Flex, Box } from "@chakra-ui/react";
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { QuillBinding } from 'y-quill';
import { dbgLog } from "../utils/appDebugFlags";
import { trackUserEvent } from "../utils/userEventTracker";
import { capitalizeDictatedSegment } from "../utils/voiceDictationFormatting";
import VoiceTyping from "../components/VoiceTyping";

const vdLog = (...args) => dbgLog("viewable-document", ...args);

const ViewableDocument = ({ onClick, id, name, patient_name, date_, type, time_, ...otherFields }) => {
  const editorRef = useRef(null);
  const location = useLocation();
  const toast = useToast();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [safeShareMode, setSafeShareMode] = useState(false);
  const [safeShareMethod, setSafeShareMethod] = useState("mask"); // "mask", "redact", or "hash"
  const [isDeidentifying, setIsDeidentifying] = useState(false);
  const [originalContents, setOriginalContents] = useState(null);
  const contentChangeTimeout = useRef(null);
  
  // Multi-selection state for multi-highlighting functionality
  const [multiSelections, setMultiSelections] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiSelectIndicator, setShowMultiSelectIndicator] = useState(false);
  
  // WebSocket collaboration state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const bindingRef = useRef(null);
  const providerRef = useRef(null);
  const websocketSetupLock = useRef(false);
  const documentLoadedRef = useRef(false);
  const [oversizedPreview, setOversizedPreview] = useState(null);

  vdLog("ViewableDocument - Full Props:", { id, name, patient_name, date_, type, time_, ...otherFields });
  vdLog("ViewableDocument - Props breakdown:", {
    id: id,
    name: name,
    patient_name: patient_name,
    date_: date_,
    type: type,
    time_: time_,
    otherFields: otherFields
  });

  // Use patient_name as fallback
  const documentName = name || patient_name || "Untitled";

  // Use the id when constructing the filename
  const fileName = `${name}-${id}.json`;
  const [filePath, setFilePath] = React.useState(null);
  
  // Generate file path with schema support
  React.useEffect(() => {
    const generatePath = async () => {
      const year = date_?.split('-')[0];
      if (year && date_) {
        const path = await generateMedADocsPath(year, date_, fileName);
        setFilePath(path);
      }
    };
    generatePath();
  }, [date_, fileName]);

  vdLog("ViewableDocument - Location State:", location.state);
  vdLog("ViewableDocument - Extracted Values:", { 
    name, 
    patient_name,
    documentName,
    date_, 
    id,
    type,
    time_
  });

  // Fetch and initialize document
  useEffect(() => {
    const fetchAndSetDocument = async () => {
      if (safeShareMode) {
        return;
      }
      try {
        if (!date_) {
          console.error("date_ is missing from location state");
          return;
        }

        // Wait for filePath to be generated
        if (!filePath) {
          return;
        }

        vdLog("ViewableDocument - File Info:", { fileName, filePath });

        setOversizedPreview(null);
        const documentContent = await fetchDocument(bucketName_files, filePath);

        if (
          documentContent?.storageJsonTooLarge &&
          documentContent?.storageJsonOfferCreateNew !== false
        ) {
          setOversizedPreview({
            message:
              documentContent.storageJsonTooLargeMessage ||
              "This file exceeds the size limit for in-browser viewing.",
          });
        }

        if (!editorRef.current) {
          // Register QuillCursors module for collaboration
          Quill.register('modules/cursors', QuillCursors);
          
          const quill = new Quill("#viewable-doc", {
            theme: "snow",
            readOnly: !isEditMode,
            modules: {
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['link'],
                ['clean']
              ],
              history: {
                delay: 1000,
                maxStack: 100,
                userOnly: false
              },
              cursors: true // Enable cursors module for collaboration
            },
          });
          
          // Add undo/redo buttons to toolbar after Quill is initialized
          const toolbar = quill.getModule('toolbar');
          if (toolbar) {
            // Create undo button
            const undoButton = document.createElement('button');
            undoButton.setAttribute('type', 'button');
            undoButton.setAttribute('title', 'Undo (Ctrl+Z)');
            undoButton.className = 'ql-undo';
            undoButton.style.cssText = 'width: 28px; height: 28px; display: inline-block; cursor: pointer;';
            undoButton.innerHTML = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="6 10 4 12 2 10"></polyline><path class="ql-stroke" d="M2.5,10.5L2.5,6.5a5.5,5.5,0,0,1,11,0v1"></path></svg>';
            undoButton.onclick = (e) => {
              e.preventDefault();
              quill.history.undo();
            };
            
            // Create redo button
            const redoButton = document.createElement('button');
            redoButton.setAttribute('type', 'button');
            redoButton.setAttribute('title', 'Redo (Ctrl+Y / Cmd+Shift+Z)');
            redoButton.className = 'ql-redo';
            redoButton.style.cssText = 'width: 28px; height: 28px; display: inline-block; cursor: pointer;';
            redoButton.innerHTML = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="12 10 14 12 16 10"></polyline><path class="ql-stroke" d="M15.5,10.5L15.5,6.5a5.5,5.5,0,0,0-11,0v1"></path></svg>';
            redoButton.onclick = (e) => {
              e.preventDefault();
              quill.history.redo();
            };
            
            // Wrap buttons in a span to match Quill's format structure
            const buttonGroup = document.createElement('span');
            buttonGroup.className = 'ql-formats';
            buttonGroup.appendChild(undoButton);
            buttonGroup.appendChild(redoButton);
            
            // Insert undo/redo buttons at the beginning of toolbar
            const toolbarContainer = toolbar.container;
            if (toolbarContainer) {
              const firstChild = toolbarContainer.firstChild;
              if (firstChild) {
                toolbarContainer.insertBefore(buttonGroup, firstChild);
              } else {
                toolbarContainer.appendChild(buttonGroup);
              }
            }
            
            // Hide toolbar initially if not in edit mode
            toolbar.container.style.display = isEditMode ? 'block' : 'none';
          }

          quill.root.style.maxWidth = "100%";
          quill.root.style.padding = "10px";
          quill.root.style.overflowWrap = "break-word";
          quill.root.style.whiteSpace = "pre-wrap";
          
          // Track content changes
          quill.on('text-change', () => {
            setHasChanges(prev => {
              // Only mark as changed if in edit mode
              return isEditMode ? true : prev;
            });
          });
          
          editorRef.current = quill;

          if (documentContent) {
            quill.setContents(documentContent.delta || {});
            setHasChanges(false);
          }
          
          documentLoadedRef.current = true;
        } else {
          // Editor already exists - update content if needed
          if (documentContent) {
            const currentContent = editorRef.current.getContents();
            const newContent = documentContent.delta || {};
            // Only update if content actually changed
            if (JSON.stringify(currentContent) !== JSON.stringify(newContent)) {
              editorRef.current.setContents(newContent);
              setHasChanges(false);
            }
          }
          
          documentLoadedRef.current = true;
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      }
    };
    fetchAndSetDocument();
  }, [date_, id, documentName, filePath, isEditMode, safeShareMode]);

  // Apply multi-selection highlighting
  const applyMultiSelectionHighlighting = useCallback(() => {
    if (!editorRef.current || !isEditMode) return;
    
    const editor = editorRef.current;
    
    // Remove existing highlights
    const existingHighlights = editor.container.querySelectorAll('.multi-selection-highlight');
    existingHighlights.forEach(highlight => highlight.remove());
    
    // Apply highlighting to each selection
    multiSelections.forEach((selection, index) => {
      try {
        const bounds = editor.getBounds(selection.index, selection.length);
        if (bounds) {
          const highlight = document.createElement('div');
          highlight.className = 'multi-selection-highlight';
          highlight.style.position = 'absolute';
          highlight.style.left = `${bounds.left}px`;
          highlight.style.top = `${bounds.top}px`;
          highlight.style.width = `${bounds.width}px`;
          highlight.style.height = `${bounds.height}px`;
          highlight.style.backgroundColor = `rgba(33, 150, 243, 0.3)`;
          highlight.style.border = 'none';
          highlight.style.borderRadius = '0';
          highlight.style.pointerEvents = 'auto';
          highlight.style.zIndex = '1000';
          highlight.style.cursor = 'move';
          highlight.setAttribute('data-selection-id', selection.id);
          
          const editorContainer = editor.container;
          if (editorContainer) {
            editorContainer.appendChild(highlight);
          }
        }
      } catch (error) {
        console.warn('Could not highlight selection:', error);
      }
    });
  }, [multiSelections, isEditMode]);

  // Update highlighting when selections change (only in edit mode)
  useEffect(() => {
    if (isEditMode) {
      const timer = setTimeout(() => {
        applyMultiSelectionHighlighting();
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      // Clear highlights when not in edit mode
      if (editorRef.current) {
        const existingHighlights = editorRef.current.container?.querySelectorAll('.multi-selection-highlight');
        existingHighlights?.forEach(highlight => highlight.remove());
      }
    }
  }, [applyMultiSelectionHighlighting, isEditMode]);

  // Handle save
  const handleSave = useCallback(async (isAutoSave = false) => {
    if (!editorRef.current) {
      if (!isAutoSave) {
        toast({
          title: "Error",
          description: "Editor not available",
          status: "error",
          duration: 3000,
        });
      }
      return;
    }

    setIsSaving(true);

    try {
      const supabase = await getSupabase();
      const editor = editorRef.current;
      const currentDelta = editor.getContents();

      const dataToSave = {
        content: currentDelta,
        delta: currentDelta,
        lastUpdated: new Date().toISOString(),
      };

      const file = new Blob([JSON.stringify(dataToSave, null, 2)], {
        type: "application/json",
      });

      const { error } = await supabase.storage
        .from(bucketName_files)
        .upload(filePath, file, {
          upsert: true,
          contentType: "application/json",
        });

      if (error) {
        console.error("Error uploading file:", error.message);
        if (!isAutoSave) {
          toast({
            title: "Error saving document",
            description: error.message,
            status: "error",
            duration: 3000,
          });
        }
      } else {
        vdLog(`File successfully saved at: ${filePath}${isAutoSave ? ' (auto-saved)' : ''}`);
        setHasChanges(false);
        trackUserEvent('document_edit', {
          metadata: { document_id: id || filePath || 'unknown', auto_save: !!isAutoSave },
        });
        if (!isAutoSave) {
          toast({
            title: "Document saved",
            description: "Your changes have been saved successfully.",
            status: "success",
            duration: 3000,
          });
        }
      }
    } catch (err) {
      console.error("Error saving document:", err.message);
      if (!isAutoSave) {
        toast({
          title: "Error saving document",
          description: err.message,
          status: "error",
          duration: 3000,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [filePath, toast, id]);

  // Voice-typing interim preview range (so we can replace it on each update)
  const voiceInterimRangeRef = useRef(null);

  // Insert transcribed text at the cursor. (text, { isFinal, keepInterim }).
  // keepInterim=true (sent on stop) drops the tracking ref so the rendered
  // preview stays in the document instead of being deleted.
  const handleVoiceTypingResult = useCallback((transcriptText, opts = {}) => {
    if (opts.keepInterim) {
      if (voiceInterimRangeRef.current) {
        setHasChanges(true);
      }
      voiceInterimRangeRef.current = null;
      return;
    }
    const isFinal = opts.isFinal !== false;
    const editor = editorRef.current;
    if (!editor) return;

    if (voiceInterimRangeRef.current) {
      const { index, length } = voiceInterimRangeRef.current;
      if (length > 0) {
        try { editor.deleteText(index, length, "user"); } catch (_) {}
      }
      voiceInterimRangeRef.current = null;
    }

    if (!transcriptText) return;

    const selection = editor.getSelection();
    const cursorPosition = selection ? selection.index : editor.getLength();

    if (isFinal) {
      const capitalized = capitalizeDictatedSegment(editor, cursorPosition, transcriptText);
      const text = capitalized + " ";
      editor.insertText(cursorPosition, text, "user");
      editor.setSelection(cursorPosition + text.length);
      setHasChanges(true);
    } else {
      editor.insertText(cursorPosition, transcriptText, "user");
      voiceInterimRangeRef.current = {
        index: cursorPosition,
        length: transcriptText.length,
      };
      editor.setSelection(cursorPosition + transcriptText.length);
    }
  }, []);

  // Copy functionality for multi-selections
  const handleCopyMultiSelection = useCallback(() => {
    if (!editorRef.current || multiSelections.length === 0) return;
    
    const editor = editorRef.current;
    
    try {
      // Get all selected content with formatting
      const selectedContents = multiSelections.map(selection => {
        return editor.getContents(selection.index, selection.length);
      });
      
      // Combine all deltas into a single delta
      let combinedDelta = { ops: [] };
      selectedContents.forEach((delta, index) => {
        if (index > 0) {
          // Add a newline between selections to preserve structure
          combinedDelta.ops.push({ insert: '\n' });
        }
        combinedDelta.ops.push(...delta.ops);
      });
      
      // Get plain text as fallback
      const selectedTexts = multiSelections.map(selection => {
        return editor.getText(selection.index, selection.length);
      });
      const textContent = selectedTexts.join('\n');
      
      // Copy to clipboard
      navigator.clipboard.writeText(textContent).then(() => {
        vdLog('Multi-selection copied to clipboard');
        toast({
          title: "Copied",
          description: `${multiSelections.length} selection(s) copied`,
          status: "success",
          duration: 2000,
        });
      }).catch(err => {
        console.error('Failed to copy:', err);
        toast({
          title: "Copy failed",
          description: err.message,
          status: "error",
          duration: 3000,
        });
      });
      
    } catch (error) {
      console.error('Error in copy operation:', error);
      toast({
        title: "Copy error",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  }, [multiSelections, toast]);

  // Cut functionality for multi-selections
  const handleCutMultiSelection = useCallback(() => {
    if (!editorRef.current || multiSelections.length === 0) return;
    
    const editor = editorRef.current;
    
    // First copy the text
    handleCopyMultiSelection();
    
    // Batch all deletions into a single undo operation
    // This ensures Ctrl+Z will undo all deletions at once
    const history = editor.history;
    history.cutoff(); // End current undo group
    
    // Then delete the selections (in reverse order to maintain indices)
    const sortedSelections = [...multiSelections].sort((a, b) => b.index - a.index);
    
    sortedSelections.forEach(selection => {
      editor.deleteText(selection.index, selection.length, 'user');
    });
    
    history.cutoff(); // End this undo group so all deletions are treated as one operation
    
    // Clear multi-selections after cutting
    setMultiSelections([]);
    setHasChanges(true);
  }, [multiSelections, handleCopyMultiSelection]);

  // Handle edit mode changes
  useEffect(() => {
    if (editorRef.current) {
      const activeEdit = isEditMode && !safeShareMode;
      editorRef.current.enable(activeEdit);
      const toolbar = editorRef.current.getModule('toolbar');
      if (toolbar) {
        toolbar.container.style.display = activeEdit ? 'block' : 'none';
      }
      
      // Update text-change handler to track changes in edit mode
      if (activeEdit) {
        editorRef.current.off('text-change');
        editorRef.current.on('text-change', () => {
          setHasChanges(true);
          // Trigger auto-save after a short delay
          if (contentChangeTimeout.current) {
            clearTimeout(contentChangeTimeout.current);
          }
          contentChangeTimeout.current = setTimeout(() => {
            handleSave(true); // Pass true to indicate auto-save
          }, 2000); // Auto-save after 2 seconds of no typing
        });
      } else {
        // Clear multi-selections when exiting edit mode
        setMultiSelections([]);
        setIsMultiSelectMode(false);
        setShowMultiSelectIndicator(false);
        // Clear any pending auto-save
        if (contentChangeTimeout.current) {
          clearTimeout(contentChangeTimeout.current);
          contentChangeTimeout.current = null;
        }
      }
    }
  }, [isEditMode, safeShareMode, handleSave]);

  // Multi-selection keyboard and mouse event handlers
  useEffect(() => {
    if (!isEditMode) return; // Only enable in edit mode
    
    const handleKeyDown = (event) => {
      // Check for Cmd/Ctrl key to enable multi-select mode
      if (event.metaKey || event.ctrlKey) {
        setIsMultiSelectMode(true);
        setShowMultiSelectIndicator(true);
      }
      
      // Handle copy/cut shortcuts for multi-selections
      if ((event.metaKey || event.ctrlKey) && multiSelections.length > 0) {
        switch (event.key.toLowerCase()) {
          case 'c':
            event.preventDefault();
            handleCopyMultiSelection();
            break;
          case 'x':
            event.preventDefault();
            handleCutMultiSelection();
            break;
        }
      }
      
      // Escape key to clear all selections
      if (event.key === 'Escape') {
        setMultiSelections([]);
        setIsMultiSelectMode(false);
        setShowMultiSelectIndicator(false);
      }
    };

    const handleKeyUp = (event) => {
      // Disable multi-select mode when Cmd/Ctrl is released
      if (!event.metaKey && !event.ctrlKey) {
        setIsMultiSelectMode(false);
        setShowMultiSelectIndicator(false);
      }
    };

    const handleMouseUp = (event) => {
      // Ignore clicks on toolbar buttons and other UI elements
      if (event.target.closest('.ql-toolbar') ||
          event.target.closest('button') ||
          event.target.closest('select') ||
          event.target.closest('.multi-selection-highlight')) {
        return;
      }
      
      // Check if the click was on the editor
      const editorElement = editorRef.current?.root;
      if (!editorElement || !editorElement.contains(event.target)) {
        // Clicked outside editor - clear all multi-selections if not in multi-select mode
        if (!isMultiSelectMode) {
          setMultiSelections([]);
        }
        return;
      }
      
      if (!editorRef.current) return;
      
      const selection = editorRef.current.getSelection();
      
      // If not in multi-select mode, clear all multi-selections
      if (!isMultiSelectMode) {
        setMultiSelections([]);
        return;
      }
      
      // If no selection or empty selection, don't add to multi-selections
      if (!selection || selection.length === 0) {
        return;
      }
      
      // Add or remove selection from multi-selections
      const newSelection = {
        index: selection.index,
        length: selection.length,
        id: Date.now() + Math.random() // Unique ID for this selection
      };
      
      setMultiSelections(prev => {
        // Check if this selection overlaps with any existing selection
        const hasOverlap = prev.some(sel => 
          (newSelection.index >= sel.index && newSelection.index < sel.index + sel.length) ||
          (sel.index >= newSelection.index && sel.index < newSelection.index + newSelection.length)
        );
        
        if (hasOverlap) {
          // Remove overlapping selections
          return prev.filter(sel => 
            !((newSelection.index >= sel.index && newSelection.index < sel.index + sel.length) ||
              (sel.index >= newSelection.index && sel.index < newSelection.index + newSelection.length))
          );
        } else {
          // Add new selection
          return [...prev, newSelection];
        }
      });
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isEditMode, isMultiSelectMode, multiSelections, handleCopyMultiSelection, handleCutMultiSelection]);

  // Toggle edit mode
  const toggleEditMode = async () => {
    // If exiting edit mode and there are unsaved changes, save first
    if (isEditMode && hasChanges) {
      // Save before switching to view mode
      await handleSave(false);
    }
    setIsEditMode(!isEditMode);
    setHasChanges(false);
    // Clear multi-selections when exiting edit mode
    if (isEditMode) {
      setMultiSelections([]);
      setIsMultiSelectMode(false);
      setShowMultiSelectIndicator(false);
      // Clear any pending auto-save
      if (contentChangeTimeout.current) {
        clearTimeout(contentChangeTimeout.current);
        contentChangeTimeout.current = null;
      }
    }
  };

  // Toggle Safe-Share Mode (HIPAA Screen Masking)
  const toggleSafeShareMode = async () => {
    if (!editorRef.current) return;

    if (safeShareMode) {
      // Toggling OFF: Restore original content
      if (originalContents) {
        editorRef.current.setContents(originalContents);
      }
      setOriginalContents(null);
      setSafeShareMode(false);
      
      toast({
        title: "Safe-Share Disabled",
        description: "Original document content restored.",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      // Toggling ON:
      // Save unsaved changes first if in edit mode
      if (isEditMode && hasChanges) {
        await handleSave(false);
      }

      setIsDeidentifying(true);
      try {
        const editor = editorRef.current;
        const currentContents = editor.getContents();
        const plainText = editor.getText();

        // Call the Django de-identification service
        const backendUrl = getBackendURL();
        vdLog("Calling de-identification service on Django backend:", `${backendUrl}/api/deidentify/`);
        
        const response = await fetch(`${backendUrl}/api/deidentify/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: plainText,
            method: safeShareMethod, // "mask", "redact", or "hash"
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to de-identify text: ${response.statusText}`);
        }

        const data = await response.json();
        const deidentifiedText = data.deidentified_text;

        // Explicitly destroy Yjs binding and websocket provider BEFORE changing editor text
        // to prevent syncing masked text to other collaborative clients!
        if (bindingRef.current) {
          vdLog("Destroying QuillBinding to prevent syncing masked text");
          bindingRef.current.destroy();
          bindingRef.current = null;
        }
        if (providerRef.current) {
          vdLog("Destroying WebsocketProvider to prevent syncing masked text");
          providerRef.current.destroy();
          providerRef.current = null;
        }
        setIsConnected(false);
        setConnectionStatus('disconnected');
        websocketSetupLock.current = false;

        // Store original contents
        setOriginalContents(currentContents);
        
        // Turn off edit mode
        setIsEditMode(false);
        
        // Update editor text
        editor.setText(deidentifiedText);
        setSafeShareMode(true);

        toast({
          title: "Safe-Share Enabled",
          description: `Document de-identified using '${safeShareMethod}' method. Auto-save disabled.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error enabling Safe-Share mode:", error);
        toast({
          title: "Safe-Share Error",
          description: `Failed to de-identify document: ${error.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsDeidentifying(false);
      }
    }
  };

  // WebSocket collaboration effect - only when in edit mode
  useEffect(() => {
    if (!isEditMode || !editorRef.current || !documentLoadedRef.current) {
      // Clean up WebSocket if exiting edit mode
      if (!isEditMode && bindingRef.current) {
        vdLog('🔌 Exiting edit mode - cleaning up WebSocket');
        if (bindingRef.current) {
          bindingRef.current.destroy();
          bindingRef.current = null;
        }
        if (providerRef.current) {
          providerRef.current.destroy();
          providerRef.current = null;
        }
        setIsConnected(false);
        setConnectionStatus('disconnected');
        websocketSetupLock.current = false;
      }
      return;
    }

    const editor = editorRef.current;
    
    // Check if document has content
    const hasSubstantialContent = editor.getLength() > 1;
    if (!hasSubstantialContent) {
      vdLog('🔌 WebSocket: Document appears empty, waiting for content...');
      return;
    }

    // Check WebSocket setup lock to prevent multiple setups
    if (websocketSetupLock.current) {
      vdLog('🔒 WebSocket setup lock active, skipping duplicate setup');
      return;
    }
    websocketSetupLock.current = true;

    vdLog('🔌 WebSocket: Setting up collaboration for ViewableDocument:', filePath);

    let wsProvider = null;
    let isCleaningUp = false;
    let connectionTimeout = null;

    const setupWebSocket = () => {
      if (isCleaningUp) return;

      try {
        const ydoc = new Y.Doc();
        const ytext = ydoc.getText('quill');
        const encodedRoom = encodeURIComponent(filePath);
        const wsUrl = getWebSocketURL();
        
        vdLog('Setting up WebSocket collaboration:', { filePath, encodedRoom, wsUrl });
        
        // Validate WebSocket URL
        if (!wsUrl || wsUrl === 'undefined' || wsUrl === 'null') {
          console.error('❌ Invalid WebSocket URL:', wsUrl);
          setIsConnected(false);
          return;
        }
        
        setConnectionStatus('connecting');
        setIsConnected(true);
        
        connectionTimeout = setTimeout(() => {
          if (connectionStatus === 'connecting' && !isCleaningUp) {
            vdLog('⏰ Connection timeout warning');
          }
        }, 5000);
        
        wsProvider = new WebsocketProvider(
          wsUrl,
          encodedRoom,
          ydoc,
          {
            connect: true,
            maxBackoffTime: 5000,
            disableBc: true
          }
        );
        
        // Make WebSocket provider globally available
        window.wsProvider = wsProvider;
        providerRef.current = wsProvider;

        wsProvider.on('status', ({ status }) => {
          if (isCleaningUp) return;
          
          setConnectionStatus(status);
          
          if (status === 'disconnected') {
            setTimeout(() => {
              if (status === 'disconnected' && !isCleaningUp) {
                setIsConnected(false);
              }
            }, 3000);
          } else if (status === 'connecting') {
            setIsConnected(true);
          }
          
          if (status === 'connected') {
            setIsConnected(true);
            if (connectionTimeout) {
              clearTimeout(connectionTimeout);
              connectionTimeout = null;
            }
            
            if (!bindingRef.current) {
              try {
                const hostname = window.location.hostname;
                const bindingDelay = hostname === 'localhost' ? 200 : 500;
                
                setTimeout(async () => {
                  if (!bindingRef.current && !isCleaningUp) {
                    // Get current editor content
                    const currentContent = editor.getContents();
                    
                    // Wait for Yjs to sync
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Check if Yjs document is empty
                    const yjsContent = ytext.toString();
                    const editorContent = currentContent ? currentContent.ops?.map(op => op.insert).filter(Boolean).join('') : '';
                    
                    // Only populate Yjs if it's empty and we have editor content
                    if (ytext.length === 0 && currentContent && currentContent.ops && currentContent.ops.length > 0) {
                      vdLog('Yjs document is empty - populating with editor content');
                      currentContent.ops.forEach(op => {
                        if (op.insert) {
                          if (typeof op.insert === 'string') {
                            ytext.insert(ytext.length, op.insert, op.attributes || {});
                          } else if (op.insert && typeof op.insert === 'object') {
                            // Yjs expects (index, embedObject, attributes); op.insert is already
                            // the embed object (e.g. { image: url }) per Quill's delta format.
                            ytext.insertEmbed(ytext.length, op.insert, op.attributes || {});
                          }
                        }
                      });
                    }
                    
                    // Create binding - check if awareness exists
                    if (!wsProvider || !wsProvider.awareness) {
                      console.error('❌ WebSocket provider or awareness not available');
                      return;
                    }
                    
                    bindingRef.current = new QuillBinding(ytext, editor, wsProvider.awareness);
                    vdLog('Quill binding established for ViewableDocument');
                    
                    // Set up user awareness
                    const setupUserAwareness = async () => {
                      try {
                        if (!wsProvider || !wsProvider.awareness) {
                          console.warn('⚠️ WebSocket provider or awareness not available for user awareness setup');
                          return;
                        }
                        
                        const supabase = await getSupabase();
                        const { data: { session } } = await supabase.auth.getSession();
                        const user = session?.user;
                        
                        if (user) {
                          const { data: userData } = await supabase
                            .from('users')
                            .select('name')
                            .eq('id', user.id)
                            .single();
                          
                          const userName = userData?.name || user.email || 'Anonymous';
                          
                          wsProvider.awareness.setLocalStateField('user', {
                            name: userName,
                            color: generateNonBlueColor(),
                            id: user.id
                          });
                          
                          wsProvider.awareness.setLocalStateField('is_typing', false);
                          wsProvider.awareness.setLocalStateField('cursor_active', false);
                          
                          vdLog('User awareness set for ViewableDocument:', userName);
                        }
                      } catch (error) {
                        console.error('Error setting up user awareness:', error);
                      }
                    };
                    
                    setupUserAwareness();
                    
                    // Handle awareness updates for cursor colors
                    if (wsProvider && wsProvider.awareness) {
                    wsProvider.awareness.on('change', () => {
                      setTimeout(() => {
                        const cursorElements = document.querySelectorAll('.ql-cursor');
                        const states = wsProvider.awareness.getStates();
                        
                        cursorElements.forEach((cursorEl) => {
                          const cursorName = cursorEl.querySelector('.ql-cursor-name');
                          if (cursorName) {
                            for (const [clientId, state] of states) {
                              if (state.user && state.user.name === cursorName.textContent) {
                                cursorName.style.backgroundColor = state.user.color;
                                cursorName.style.background = state.user.color;
                                cursorEl.style.setProperty('border-left-color', state.user.color, 'important');
                                
                                const cursorCarets = cursorEl.querySelectorAll('.ql-cursor-caret');
                                cursorCarets.forEach(caret => {
                                  caret.style.setProperty('border-left-color', state.user.color, 'important');
                                });
                                break;
                              }
                            }
                          }
                        });
                      }, 100);
                    });
                    }
                  }
                }, bindingDelay);
              } catch (bindError) {
                console.error('Error binding Quill:', bindError);
              }
            }
          }
        });
        
      } catch (error) {
        console.error('❌ Error setting up WebSocket:', error);
        setIsConnected(false);
      }
    };
    
    // Environment-specific delay
    const hostname = window.location.hostname;
    const setupDelay = hostname === 'localhost' ? 300 : 500;
    setTimeout(setupWebSocket, setupDelay);
    
    return () => {
      vdLog('🧹 WebSocket Cleanup for ViewableDocument');
      
      isCleaningUp = true;
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }
      if (bindingRef.current) {
        bindingRef.current.destroy();
        bindingRef.current = null;
      }
      if (wsProvider) {
        wsProvider.destroy();
      }
      if (providerRef.current) {
        providerRef.current.destroy();
        providerRef.current = null;
      }
      setIsConnected(false);
      setConnectionStatus('disconnected');
      websocketSetupLock.current = false;
    };
  }, [isEditMode, filePath]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (contentChangeTimeout.current) {
        clearTimeout(contentChangeTimeout.current);
      }
      // Clean up WebSocket on unmount
      if (bindingRef.current) {
        bindingRef.current.destroy();
        bindingRef.current = null;
      }
      if (providerRef.current) {
        providerRef.current.destroy();
        providerRef.current = null;
      }
    };
  }, []);

  // Return early if no date_ — can't build a file path without it
  if (!date_) {
    return <div style={{ color: "#888", padding: "8px", fontSize: "13px" }}>No document (appointment date not set)</div>;
  }

  const buildClickPayload = () => ({
    filePath,
    name,
    date_,
    id,
    uuid: id,
    type,
    time_,
    ...otherFields,
  });

  const handleClick = () => {
    // Only navigate on click if NOT in edit mode
    if (!isEditMode) {
    vdLog("🔍 ViewableDocument - handleClick called with:", {
      filePath,
      name,
      date_,
      id,
      type,
      time_,
      otherFields
    });
    onClick(buildClickPayload());
    }
  };

  return (
    <Box>
      <Modal
        isOpen={!!oversizedPreview}
        onClose={() => setOversizedPreview(null)}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>File too large to load from storage</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              This document was not downloaded because it exceeds the size
              limit. You can open the full editor with a new blank template
              instead (the usual template for this visit type). Saving will
              replace the file at this path when you save.
            </Text>
            <Text fontSize="sm" color="gray.600">
              {oversizedPreview?.message}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setOversizedPreview(null)}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                setOversizedPreview(null);
                onClick({
                  ...buildClickPayload(),
                  skipLargeStorageJsonFetch: true,
                });
              }}
            >
              Create new
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex gap={2} alignItems="center" mb={3} flexWrap="wrap">
        <Button
          size="sm"
          colorScheme={isEditMode ? "gray" : "blue"}
          onClick={toggleEditMode}
          isDisabled={safeShareMode}
        >
          {isEditMode ? "View Mode" : "Edit Mode"}
        </Button>

        {/* Safe-Share Mode Controls */}
        <Flex gap={1} alignItems="center">
          <Button
            size="sm"
            colorScheme={safeShareMode ? "teal" : "gray"}
            variant={safeShareMode ? "solid" : "outline"}
            onClick={toggleSafeShareMode}
            isLoading={isDeidentifying}
            loadingText="Processing..."
          >
            {safeShareMode ? "🔒 Safe-Share Active" : "🔓 Safe-Share"}
          </Button>
          {!safeShareMode && (
            <Select
              size="sm"
              width="100px"
              value={safeShareMethod}
              onChange={(e) => setSafeShareMethod(e.target.value)}
              isDisabled={isDeidentifying}
            >
              <option value="mask">Mask</option>
              <option value="redact">Redact</option>
              <option value="hash">Hash</option>
            </Select>
          )}
        </Flex>

        {isEditMode && (
          <>
            <Button
              size="sm"
              colorScheme="green"
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
              isDisabled={isSaving}
            >
              💾 Save
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              onClick={() => onClick(buildClickPayload())}
            >
              📝 Open in Full Editor
            </Button>
            <VoiceTyping
              onTranscriptionResult={handleVoiceTypingResult}
              isEnabled={true}
              size="sm"
            />
            {hasChanges && (
              <Box fontSize="xs" color="orange.500" alignSelf="center" ml={2}>
                Unsaved changes
              </Box>
            )}
            {multiSelections.length > 0 && (
              <Box fontSize="xs" color="blue.600" alignSelf="center" ml={2}>
                {multiSelections.length} selection{multiSelections.length !== 1 ? 's' : ''}
              </Box>
            )}
            {showMultiSelectIndicator && (
              <Box 
                fontSize="xs" 
                color="blue.600" 
                bg="blue.50"
                px={2}
                py={1}
                borderRadius="md"
                fontWeight="medium"
                ml={2}
              >
                🖱️ Multi-Select Mode (Cmd/Ctrl + Click to select multiple)
              </Box>
            )}
          </>
        )}
      </Flex>
      <div 
        className="viewable-doc-container" 
        onClick={handleClick}
        style={{ cursor: isEditMode ? 'text' : 'pointer' }}
      >
      <div className="document-page">
        <div id="viewable-doc"></div>
      </div>
    </div>
    </Box>
  );
};

export default ViewableDocument;
